import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { IonButton, IonSpinner, IonIcon } from '@ionic/react';
import {
  bugOutline,
  alertCircleOutline,
  warningOutline,
  informationCircleOutline,
  skullOutline,
  linkOutline,
  rocketOutline,
} from 'ionicons/icons';
import { Helmet } from 'react-helmet-async';
import api from '../../services/api';
import './SentryIssueDetailsPage.css';

const getLevelIcon = (level) => {
  switch (level) {
    case 'fatal': return skullOutline;
    case 'error': return alertCircleOutline;
    case 'warning': return warningOutline;
    case 'info': return informationCircleOutline;
    default: return bugOutline;
  }
};

const getLevelColor = (level) => {
  switch (level) {
    case 'fatal': return '#ff4d4f';
    case 'error': return '#f87168';
    case 'warning': return '#faad14';
    case 'info': return '#579dff';
    default: return '#6b778c';
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'critical': return '#ff4d4f';
    case 'high': return '#f87168';
    case 'medium': return '#faad14';
    case 'low': return '#52c41a';
    default: return '#6b778c';
  }
};

const formatRelativeTime = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (dateInput) => {
  if (!dateInput) return '';
  return new Date(dateInput).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const SentryIssueDetailsPage = () => {
  const { projectId, issueId } = useParams();
  const navigate = useNavigate();
  const { setAnalyzeModalOpen } = useOutletContext();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    loadIssue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId]);

  const loadIssue = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get(`/sentry/issues/${issueId}`);
      setIssue(data);
    } catch (err) {
      console.error('Error loading Sentry issue:', err);
      setError(err.message || 'Failed to load issue');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="sentry-detail-loading">
        <IonSpinner />
      </div>
    );
  }

  if (error || !issue) {
    return (
      <div className="sentry-detail-error">
        <h2>Failed to load issue</h2>
        <p>{error || 'Issue not found'}</p>
        <IonButton onClick={() => navigate(`/projects/${projectId}/issues`)}>
          Back to Issues
        </IonButton>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{issue.shortId}: {issue.title} - Zibby</title>
      </Helmet>
      <div className="sentry-detail-page breadcrumb-spacing">
        {/* Header */}
        <div className="sentry-detail-header">
          <div className="sentry-title-section">
            <div className="sentry-title-row">
              <IonIcon
                icon={getLevelIcon(issue.level)}
                className="sentry-level-icon"
                style={{ color: getLevelColor(issue.level) }}
              />
              <h1 className="sentry-title">{issue.title}</h1>
              {issue.permalink && (
                <a
                  href={issue.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sentry-external-link"
                >
                  <IonIcon icon={linkOutline} />
                  <span className="instant-tooltip">Open in Sentry</span>
                </a>
              )}
              <IonButton
                onClick={() => setAnalyzeModalOpen(true)}
                className="btn-white header-action-btn"
                size="small"
                style={{ marginLeft: 'auto', flexShrink: 0 }}
              >
                <IonIcon icon={rocketOutline} slot="start" />
                Analyze
              </IonButton>
            </div>
            {issue.culprit && (
              <div className="sentry-culprit">{issue.culprit}</div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="sentry-tabs">
          <button
            className={`sentry-tab ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            Summary
          </button>
          <button
            className={`sentry-tab ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
          >
            Events ({issue.events?.length || 0})
          </button>
          <button
            className={`sentry-tab ${activeTab === 'tags' ? 'active' : ''}`}
            onClick={() => setActiveTab('tags')}
          >
            Tags
          </button>
        </div>

        <div className="sentry-detail-content">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="sentry-detail-main">
              {/* Metadata */}
              <section className="sentry-section">
                <h2 className="section-title">Details</h2>
                <div className="sentry-metadata-grid">
                  <div className="metadata-item">
                    <span className="metadata-label">Status</span>
                    <span className={`metadata-value status-${issue.status}`}>
                      {issue.status}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Priority</span>
                    <span className="metadata-value" style={{ color: getPriorityColor(issue.priority) }}>
                      {issue.priority || 'none'}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Level</span>
                    <span className="metadata-value" style={{ color: getLevelColor(issue.level) }}>
                      {issue.level}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Events</span>
                    <span className="metadata-value">{issue.count}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Users</span>
                    <span className="metadata-value">{issue.userCount}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">First Seen</span>
                    <span className="metadata-value">{formatDateTime(issue.firstSeen)}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Last Seen</span>
                    <span className="metadata-value">{formatDateTime(issue.lastSeen)}</span>
                  </div>
                  {issue.assignedTo && (
                    <div className="metadata-item">
                      <span className="metadata-label">Assigned To</span>
                      <span className="metadata-value">{issue.assignedTo.name || issue.assignedTo.email}</span>
                    </div>
                  )}
                  {issue.project && (
                    <div className="metadata-item">
                      <span className="metadata-label">Project</span>
                      <span className="metadata-value">{issue.project.name || issue.project.slug}</span>
                    </div>
                  )}
                  <div className="metadata-item">
                    <span className="metadata-label">Category</span>
                    <span className="metadata-value">{issue.issueCategory || 'error'}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Platform</span>
                    <span className="metadata-value">{issue.platform || '-'}</span>
                  </div>
                  {issue.isUnhandled && (
                    <div className="metadata-item">
                      <span className="metadata-label">Unhandled</span>
                      <span className="metadata-value" style={{ color: '#f87168' }}>Yes</span>
                    </div>
                  )}
                </div>
              </section>

              {/* Latest Event Stacktrace */}
              {issue.events && issue.events.length > 0 && (
                <section className="sentry-section">
                  <h2 className="section-title">Latest Event</h2>
                  <div className="sentry-event-detail">
                    <div className="event-header-info">
                      <span className="event-id">{issue.events[0].eventID?.slice(0, 8)}</span>
                      <span className="event-date">{formatRelativeTime(issue.events[0].dateCreated)}</span>
                    </div>
                    {issue.events[0].entries?.map((entry, idx) => {
                      if (entry.type === 'exception') {
                        return (
                          <div key={idx} className="stacktrace-section">
                            {entry.data?.values?.map((exc, excIdx) => (
                              <div key={excIdx} className="exception-block">
                                <div className="exception-type">
                                  {exc.type}: {exc.value}
                                </div>
                                {exc.stacktrace?.frames && (
                                  <div className="stacktrace-frames">
                                    {[...exc.stacktrace.frames].reverse().slice(0, 15).map((frame, fIdx) => (
                                      <div key={fIdx} className={`stacktrace-frame ${frame.inApp ? 'in-app' : ''}`}>
                                        <span className="frame-file">
                                          {frame.filename || frame.module}
                                        </span>
                                        {frame.lineNo && (
                                          <span className="frame-line">:{frame.lineNo}</span>
                                        )}
                                        {frame.function && (
                                          <span className="frame-function"> in {frame.function}</span>
                                        )}
                                        {frame.context && frame.context.length > 0 && frame.inApp && (
                                          <pre className="frame-context">
                                            {frame.context.map(([lineNo, code]) => (
                                              <div key={lineNo} className={lineNo === frame.lineNo ? 'current-line' : ''}>
                                                <span className="line-no">{lineNo}</span>
                                                <span className="line-code">{code}</span>
                                              </div>
                                            ))}
                                          </pre>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      if (entry.type === 'message') {
                        return (
                          <div key={idx} className="message-section">
                            <pre className="message-content">{entry.data?.formatted || entry.data?.message}</pre>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </section>
              )}

              {/* Activity */}
              {issue.activity && issue.activity.length > 0 && (
                <section className="sentry-section">
                  <h2 className="section-title">
                    Activity
                    <span className="section-count">{issue.activity.length}</span>
                  </h2>
                  <div className="sentry-activity-list">
                    {issue.activity.slice(0, 20).map((act, idx) => (
                      <div key={idx} className="activity-item">
                        <div className="activity-header">
                          <span className="activity-user">
                            {act.user?.name || act.user?.email || 'System'}
                          </span>
                          <span className="activity-date">{formatRelativeTime(act.dateCreated)}</span>
                        </div>
                        <div className="activity-type">{act.type}</div>
                        {act.data?.text && <div className="activity-text">{act.data.text}</div>}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="sentry-detail-main">
              {issue.events && issue.events.length > 0 ? (
                <div className="sentry-events-list">
                  {issue.events.map((event) => (
                    <div key={event.eventID} className="sentry-event-item">
                      <div className="event-item-left">
                        <span className="event-id">{event.eventID?.slice(0, 12)}</span>
                        <span className="event-title">{event.title || issue.title}</span>
                      </div>
                      <div className="event-item-right">
                        {event.user && (
                          <span className="event-user">{event.user.email || event.user.username || event.user.ip_address}</span>
                        )}
                        <span className="event-date">{formatRelativeTime(event.dateCreated)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="placeholder-text">No events loaded</div>
              )}
            </div>
          )}

          {/* Tags Tab */}
          {activeTab === 'tags' && (
            <div className="sentry-detail-main">
              {issue.tags && issue.tags.length > 0 ? (
                <div className="sentry-tags-grid">
                  {issue.tags.map((tag, idx) => (
                    <div key={idx} className="tag-card">
                      <div className="tag-key">{tag.key}</div>
                      <div className="tag-name">{tag.name || tag.key}</div>
                      <div className="tag-count">{tag.totalValues} unique values</div>
                      {tag.topValues && tag.topValues.length > 0 && (
                        <div className="tag-top-values">
                          {tag.topValues.slice(0, 5).map((v, vIdx) => (
                            <div key={vIdx} className="tag-value-item">
                              <span className="tag-value-name">{v.value}</span>
                              <span className="tag-value-count">{v.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="placeholder-text">No tags</div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SentryIssueDetailsPage;
